import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Home, MessageCircle, Target, BarChart2, User, LogOut, Smartphone, Compass, ClipboardCheck, Info } from "lucide-react";
import TeaserAuthModal from "@/components/TeaserAuthModal";

const navItems = [
  { name: "Updates", path: "/guest-poll", icon: Home },
  { name: "Chats", path: "/guest-chats", icon: MessageCircle },
  { name: "Cutoffs", path: "/guest-cutoffs", icon: BarChart2 },
  { name: "Colleges", path: "/guest-colleges", icon: Compass },
  { name: "Documents", path: "/guest-documents", icon: ClipboardCheck },
  { name: "College Predictor", path: "/guest-predictor", icon: Target },
  { name: "About Us", path: "/guest-about", icon: Info },
  { name: "Profile", path: "/guest-profile", icon: User },
];

export default function GuestLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    // Only apply on mobile where keyboard is an issue
    if (window.innerWidth >= 768) return;

    const handleFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        setIsKeyboardOpen(true);
      }
    };
    const handleFocusOut = () => {
      setIsKeyboardOpen(false);
    };

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);
    return () => {
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const handleNavClick = (path, e) => {
    e.preventDefault();
    if (path === "/guest-profile" || path === "/guest-chats") {
      setShowAuthModal(true);
    } else {
      navigate(path);
    }
  };

  return (
    <div className={`flex flex-col md:flex-row min-h-screen`} style={{ backgroundColor: '#020617', color: '#f8fafc', fontFamily: "'Outfit', sans-serif" }}>

      {/* Mobile Top Header */}
      <header className={`fixed z-40 w-full md:hidden top-0`} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(15,23,42,0.95)', backdropFilter: 'blur(12px)' }}>
        <div className="flex h-14 items-center justify-between px-4">
          <div className="font-bold text-lg tracking-tight truncate mr-2" style={{ color: '#f39c12' }}>Admissions Made Easy Portal</div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-2 border-r border-slate-700/50 pr-3 mr-1">
              <a href="tel:+917038251774" className="animate-bounce flex items-center justify-center h-8 w-8 rounded-full transition-all hover:scale-110 shadow-lg" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)', animationDelay: '0ms' }} title="Call Us">
                <i className="fas fa-phone-alt" style={{ fontSize: '0.8rem' }}></i>
              </a>
              <a href="https://wa.me/917038251774" target="_blank" rel="noopener noreferrer" className="animate-bounce flex items-center justify-center h-8 w-8 rounded-full transition-all hover:scale-110 shadow-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#4ade80', border: '1px solid rgba(34, 197, 94, 0.3)', animationDelay: '200ms' }} title="WhatsApp Us">
                <i className="fab fa-whatsapp" style={{ fontSize: '0.95rem' }}></i>
              </a>
            </div>
            <div className="h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm cursor-pointer" onClick={() => setShowAuthModal(true)} style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#94a3b8' }}>
              G
            </div>
          </div>
        </div>
      </header>

      {/* Spacer to prevent content from hiding behind fixed mobile header */}
      <div className={`w-full md:hidden shrink-0 h-[56px]`} />

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 min-h-screen sticky top-0" style={{ backgroundColor: '#0f172a', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex h-16 items-center px-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="font-extrabold text-xl tracking-tight" style={{ color: '#f39c12' }}>Admissions Made Easy Portal</div>
        </div>
        <div className="p-4 flex items-center gap-3 cursor-pointer" onClick={() => setShowAuthModal(true)} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#94a3b8' }}>
            G
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold leading-none" style={{ color: '#f8fafc' }}>Guest User</span>
            <span className="text-xs mt-1" style={{ color: '#f39c12' }}>Login to unlock</span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (location.pathname === '/' && item.path === '/guest-poll');
            return (
              <button
                key={item.name}
                onClick={(e) => handleNavClick(item.path, e)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 transition-all text-sm font-medium relative border-none cursor-pointer`}
                style={isActive ? {
                  background: 'linear-gradient(135deg, #f39c12, #d35400)',
                  color: '#fff',
                  boxShadow: '0 4px 15px rgba(243,156,18,0.3)'
                } : {
                  background: 'transparent',
                  color: '#94a3b8'
                }}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </button>
            )
          })}
        </nav>
        <div className="p-4 space-y-2 mt-auto" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            onClick={() => navigate("/")}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition-all text-sm font-medium"
            style={{ color: '#94a3b8', background: 'transparent', border: 'none', cursor: 'pointer' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <LogOut className="h-5 w-5" />
            Back to Website
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`flex-1 w-full flex flex-col pb-16 md:pb-0 overflow-y-auto relative`} style={{ backgroundColor: '#020617' }}>
        <div className={`p-4 md:p-8 w-full flex-1 flex flex-col relative`}>
          {location.pathname !== "/guest-poll" && location.pathname !== "/guest-predictor" && location.pathname !== "/download-cutoffs" && (
            <div
              className="absolute inset-0 z-50 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowAuthModal(true);
              }}
            />
          )}
          {children || <Outlet context={{ triggerAuthModal: () => setShowAuthModal(true) }} />}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className={`md:hidden fixed bottom-0 left-0 right-0 z-50 flex h-16 items-center justify-around pb-safe ${isKeyboardOpen ? 'hidden' : ''}`} style={{ borderTop: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(15,23,42,0.97)', backdropFilter: 'blur(12px)', boxShadow: '0 -4px 20px rgba(0,0,0,0.3)' }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || (location.pathname === '/' && item.path === '/guest-poll');
          return (
            <button
              key={item.name}
              onClick={(e) => handleNavClick(item.path, e)}
              className="flex flex-col items-center justify-center w-full h-full space-y-1 transition-all relative border-none bg-transparent cursor-pointer"
              style={{
                color: isActive ? '#f39c12' : '#94a3b8'
              }}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[10px] font-semibold">{item.name}</span>
            </button>
          )
        })}
      </nav>

      <TeaserAuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
